import { Link } from "@inertiajs/react"

interface Option {
  id: number
  name: string
}

interface LocationOption {
  id: number
  display_name: string
}

interface ContactFormData {
  salutation: string
  first_name: string
  middle_name: string
  last_name: string
  suffix: string
  department: string
  phone: string
  cell_phone: string
  home_phone: string
  fax: string
  email: string
  address: string
  address2: string
  city: string
  state: string
  zip: string
  notes: string
  family_info: string
  birth_month: number | null
  birth_day: number | null
  send_birthday_card: boolean
  sales_rep_id: number | null
  location_id: number | null
  status: number
}

interface Props {
  customerId: number
  data: ContactFormData
  setData: (
    key: keyof ContactFormData,
    value: string | number | boolean | null,
  ) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  users: Option[]
  locations: LocationOption[]
}

const months = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
]

const days = Array.from({ length: 31 }, (_, i) => i + 1)

export default function ContactForm({
  customerId,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  users,
  locations,
}: Props) {
  return (
    <form onSubmit={onSubmit} className="max-w-3xl">
      <div className="mb-6 grid gap-6 md:grid-cols-2">
        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Name</h2>

          <div className="mb-4 grid gap-4 md:grid-cols-4">
            <div>
              <label htmlFor="salutation" className="mb-1 block font-medium">
                Title
              </label>
              <input
                type="text"
                id="salutation"
                value={data.salutation}
                onChange={(e) => setData("salutation", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={20}
                placeholder="Mr., Ms., Dr."
              />
            </div>
            <div className="md:col-span-3">
              <label htmlFor="first_name" className="mb-1 block font-medium">
                First Name
              </label>
              <input
                type="text"
                id="first_name"
                value={data.first_name}
                onChange={(e) => setData("first_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
          </div>

          <div className="mb-4 grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="middle_name" className="mb-1 block font-medium">
                Middle
              </label>
              <input
                type="text"
                id="middle_name"
                value={data.middle_name}
                onChange={(e) => setData("middle_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="last_name" className="mb-1 block font-medium">
                Last Name
              </label>
              <input
                type="text"
                id="last_name"
                value={data.last_name}
                onChange={(e) => setData("last_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
              {errors?.last_name && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.last_name.join(", ")}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="suffix" className="mb-1 block font-medium">
                Suffix
              </label>
              <input
                type="text"
                id="suffix"
                value={data.suffix}
                onChange={(e) => setData("suffix", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={20}
                placeholder="Jr., III"
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="department" className="mb-1 block font-medium">
              Department / Title
            </label>
            <input
              type="text"
              id="department"
              value={data.department}
              onChange={(e) => setData("department", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={100}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="sales_rep_id" className="mb-1 block font-medium">
              Sales Rep
            </label>
            <select
              id="sales_rep_id"
              value={data.sales_rep_id ?? ""}
              onChange={(e) =>
                setData(
                  "sales_rep_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              <option value="">Select...</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label htmlFor="status" className="mb-1 block font-medium">
              Status
            </label>
            <select
              id="status"
              value={data.status}
              onChange={(e) => setData("status", parseInt(e.target.value))}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              <option value={0}>Active</option>
              <option value={90}>Do Not Contact</option>
            </select>
          </div>

          {locations.length > 1 && (
            <div className="mb-4">
              <label htmlFor="location_id" className="mb-1 block font-medium">
                Location
              </label>
              <select
                id="location_id"
                value={data.location_id ?? ""}
                onChange={(e) =>
                  setData(
                    "location_id",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Select...</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.display_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Contact Info</h2>

          <div className="mb-4">
            <label htmlFor="phone" className="mb-1 block font-medium">
              Office Phone
            </label>
            <input
              type="tel"
              id="phone"
              value={data.phone}
              onChange={(e) => setData("phone", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={50}
            />
          </div>

          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="cell_phone" className="mb-1 block font-medium">
                Cell Phone
              </label>
              <input
                type="tel"
                id="cell_phone"
                value={data.cell_phone}
                onChange={(e) => setData("cell_phone", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="home_phone" className="mb-1 block font-medium">
                Home Phone
              </label>
              <input
                type="tel"
                id="home_phone"
                value={data.home_phone}
                onChange={(e) => setData("home_phone", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
          </div>

          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="fax" className="mb-1 block font-medium">
                Fax
              </label>
              <input
                type="tel"
                id="fax"
                value={data.fax}
                onChange={(e) => setData("fax", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="email" className="mb-1 block font-medium">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={100}
              />
              {errors?.email && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.email.join(", ")}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Address</h2>

          <div className="mb-4">
            <label htmlFor="address" className="mb-1 block font-medium">
              Address
            </label>
            <input
              type="text"
              id="address"
              value={data.address}
              onChange={(e) => setData("address", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={100}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="address2" className="mb-1 block font-medium">
              Address 2
            </label>
            <input
              type="text"
              id="address2"
              value={data.address2}
              onChange={(e) => setData("address2", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={100}
            />
          </div>

          <div className="mb-4 grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="city" className="mb-1 block font-medium">
                City
              </label>
              <input
                type="text"
                id="city"
                value={data.city}
                onChange={(e) => setData("city", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="state" className="mb-1 block font-medium">
                State
              </label>
              <input
                type="text"
                id="state"
                value={data.state}
                onChange={(e) => setData("state", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={50}
              />
            </div>
            <div>
              <label htmlFor="zip" className="mb-1 block font-medium">
                Zip
              </label>
              <input
                type="text"
                id="zip"
                value={data.zip}
                onChange={(e) => setData("zip", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={20}
              />
            </div>
          </div>
        </div>

        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Personal</h2>

          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="birth_month" className="mb-1 block font-medium">
                Birthday Month
              </label>
              <select
                id="birth_month"
                value={data.birth_month ?? ""}
                onChange={(e) =>
                  setData(
                    "birth_month",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Select...</option>
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="birth_day" className="mb-1 block font-medium">
                Birthday Day
              </label>
              <select
                id="birth_day"
                value={data.birth_day ?? ""}
                onChange={(e) =>
                  setData(
                    "birth_day",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Select...</option>
                {days.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.send_birthday_card}
                onChange={(e) =>
                  setData("send_birthday_card", e.target.checked)
                }
                className="border-hpi-light-gray rounded"
              />
              <span className="font-medium">Send Birthday Card</span>
            </label>
          </div>

          <div className="mb-4">
            <label htmlFor="family_info" className="mb-1 block font-medium">
              Family Info
            </label>
            <textarea
              id="family_info"
              value={data.family_info}
              onChange={(e) => setData("family_info", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={2}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="notes" className="mb-1 block font-medium">
              Notes
            </label>
            <textarea
              id="notes"
              value={data.notes}
              onChange={(e) => setData("notes", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={3}
            />
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href={`/customers/${customerId}/contacts`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
